import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AccountBalanceIcon,
  CalendarIcon,
  ChatIcon,
  FoldedHandsIcon,
  ForumIcon,
  GridViewIcon,
  GroupsIcon,
  MarketIcon,
  NewspaperIcon,
  SchoolIcon,
  SuggestionIcon,
  TrophyIcon,
  VolunteeringIcon
} from '@/assets';
import { useGetOwnChats } from '@/features/api/hooks/grapevine-chat/useGetOwnChats';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import {
  MeinOrtChannel,
  MeinOrtTab,
  type MeinOrtChannelConfig
} from '@/features/mein-ort/components/meinOrtTypes';
import { useGossipChannelLabel } from '@/features/mein-ort/hooks/useGossipChannelLabel';
import type { MeinOrtChannelTeasers } from '@/features/mein-ort/hooks/useMeinOrtChannelTeasers';
import { useMeinOrtChannelTeasers } from '@/features/mein-ort/hooks/useMeinOrtChannelTeasers';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

export const useMeinOrtChannels = (): MeinOrtChannelConfig[] => {
  const channelTeasers: MeinOrtChannelTeasers = useMeinOrtChannelTeasers(true);

  const hasNewByTab: Partial<Record<MeinOrtTab, boolean>> = {
    [MeinOrtTab.NEWS]: channelTeasers.news.hasNew,
    [MeinOrtTab.PLAUSCH]: channelTeasers.plausch.hasNew,
    [MeinOrtTab.MARKT]: channelTeasers.markt.hasNew,
    [MeinOrtTab.EVENTS]: channelTeasers.events.hasNew
  };
  const { data: chats } = useGetOwnChats();
  const { isFeatureEnabled } = useFeature();
  const sagsUnsEnabled = isFeatureEnabled(FeaturesEnum.Suggestion);
  const { t } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace'
  });
  const plauschLabel = useGossipChannelLabel();
  const hasUnreadChats =
    chats?.some(
      (chat) => !chat.archived && (chat.unreadMessageCount ?? 0) > 0
    ) ?? false;

  const hasNews = hasNewByTab?.[MeinOrtTab.NEWS] ?? false;
  const hasPlausch = hasNewByTab?.[MeinOrtTab.PLAUSCH] ?? false;
  const hasMarkt = hasNewByTab?.[MeinOrtTab.MARKT] ?? false;
  const hasEvents = hasNewByTab?.[MeinOrtTab.EVENTS] ?? false;
  const hasAnyFunkzentraleNew = hasNews || hasPlausch || hasMarkt || hasEvents;

  return useMemo(
    () => [
      {
        key: MeinOrtChannel.FUNKZENTRALE,
        label: t('channels.funkzentrale'),
        tabs: [
          {
            key: MeinOrtTab.ALLE,
            label: t('tabs.alle'),
            icon: GridViewIcon,
            dot: hasAnyFunkzentraleNew
          },
          {
            key: MeinOrtTab.NEWS,
            label: t('tabs.news'),
            icon: NewspaperIcon,
            dot: hasNews
          },
          {
            key: MeinOrtTab.PLAUSCH,
            label: plauschLabel,
            icon: ForumIcon,
            dot: hasPlausch
          },
          {
            key: MeinOrtTab.MARKT,
            label: t('tabs.markt'),
            icon: MarketIcon,
            dot: hasMarkt
          },
          {
            key: MeinOrtTab.EVENTS,
            label: t('tabs.events'),
            icon: CalendarIcon,
            dot: hasEvents
          }
        ]
      },
      {
        key: MeinOrtChannel.GRUPPEN,
        label: t('channels.gruppen'),
        tabs: [
          {
            key: MeinOrtTab.GRUPPEN,
            label: t('tabs.gruppen'),
            icon: GroupsIcon
          },
          {
            key: MeinOrtTab.CHATS,
            label: t('tabs.chats'),
            icon: ChatIcon,
            dot: hasUnreadChats
          },
          ...(sagsUnsEnabled
            ? [
                {
                  key: MeinOrtTab.SAGSUNS,
                  label: t('tabs.sagsUns'),
                  icon: SuggestionIcon
                }
              ]
            : [])
        ]
      },
      {
        key: MeinOrtChannel.INFOS,
        label: t('channels.infos'),
        tabs: [
          {
            key: MeinOrtTab.TERRITORIAL_AUTHORITY,
            label: t('tabs.territorialAuthority'),
            icon: AccountBalanceIcon
          },
          {
            key: MeinOrtTab.VEREINE,
            label: t('tabs.vereine'),
            icon: TrophyIcon
          },
          {
            key: MeinOrtTab.EHRENAMT,
            label: t('tabs.ehrenamt'),
            icon: VolunteeringIcon
          },
          {
            key: MeinOrtTab.SCHULEN,
            label: t('tabs.schulen'),
            icon: SchoolIcon
          },
          {
            key: MeinOrtTab.KIRCHE,
            label: t('tabs.kirche'),
            icon: FoldedHandsIcon
          }
        ]
      }
    ],
    [
      t,
      plauschLabel,
      hasAnyFunkzentraleNew,
      hasNews,
      hasPlausch,
      hasMarkt,
      hasEvents,
      hasUnreadChats,
      sagsUnsEnabled
    ]
  );
};
